/* ============================================================
   Onboarding site prefill — shared between the /login|/signup screen
   (screens/Auth.jsx) and the onboarding chat (screens/OnboardingChat.jsx).

   The marketing landing's hero sends visitors to
   https://app.dathent.ai/login?site=<encoded website>. Auth.jsx stashes a
   validated value under PREFILL_SITE_KEY on mount (localStorage survives the
   OAuth / email-confirm redirect dance better than component state). The
   onboarding chat's URL-first greeting reads it once to prefill (never
   auto-send) its textbox, then always clears the key — regardless of
   whether it ended up applying the value — so it can never leak into a
   later session or a different flow.

   Additive only: if `site` is absent, nothing here ever runs.
   ============================================================ */

export const PREFILL_SITE_KEY = 'dathent.onboarding.prefillSite';

// Basic sanity check — a plausible bare domain or URL, optionally with a
// protocol and/or a path/query/hash tail. Not a full URL validator; just
// enough to keep obvious garbage out of localStorage / the chat textarea.
const SITE_VALUE_RE = /^[\w.-]+\.[a-z]{2,}([/?#].*)?$/i;

export function isPlausibleSiteValue(raw) {
  if (typeof raw !== 'string') return false;
  const value = raw.trim();
  if (!value || value.length > 200) return false;
  const withoutProtocol = value.replace(/^https?:\/\//i, '');
  return SITE_VALUE_RE.test(withoutProtocol);
}

// Called on mount by the /login and /signup screens. Reads `site` from a
// query string and — if it looks like a real domain/URL — stashes it for the
// onboarding chat to pick up later. Silently no-ops otherwise (missing param,
// implausible value, or localStorage unavailable/blocked).
export function captureSitePrefillFromQuery(search) {
  try {
    const raw = new URLSearchParams(search || '').get('site');
    if (!raw) return;
    const value = raw.trim();
    if (!isPlausibleSiteValue(value)) return;
    window.localStorage.setItem(PREFILL_SITE_KEY, value);
  } catch {
    // Non-fatal — onboarding just won't be prefilled.
  }
}

// Called once by the onboarding chat on mount. ALWAYS clears the key (even
// when the stashed value turns out to be missing/invalid, or the caller
// decides not to apply it) so a stale value can never leak into a later
// session or a different flow. Returns the plausible value to prefill, or
// null.
export function consumeSitePrefill() {
  let raw = null;
  try {
    raw = window.localStorage.getItem(PREFILL_SITE_KEY);
    if (raw !== null) window.localStorage.removeItem(PREFILL_SITE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  const value = raw.trim();
  return isPlausibleSiteValue(value) ? value : null;
}
